import React, { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { createEntity, friendlyError, useEntityList } from '../../data/entities';
import { useAuth } from '../../AuthContext';
import NewWorkflowForm, { DEFAULT_WORKSPACE_NAME } from '../../components/NewWorkflowForm';
import SolutionBoard from '../../components/SolutionBoard';

// nQube module: every workflow in the org, grouped by workspace, plus quick
// create. The heavy lifting (canvas, run status) lives in PipelineEditor.
// Seeded examples (pipeline.example) are learn-by-example graphs: the filter
// chip shows just those, and Duplicate copies one into an editable workflow.
export default function NQubeModule() {
  const { org, myRole } = useOutletContext();
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const [view, setView] = useState('all'); // all | examples | mine | solutions

  return (
    <div data-nova-note={`nQube workflow list (filter: ${view}) per workspace; open one to reach the logic-design canvas. NoVA: build_pipeline, describe_pipeline, edit_pipeline, declare_requirement, run_pipeline; the Solutions view is the service board (solution_status, engineer_solution).`}>
      <h1 style={{ margin: 0 }}>nQube · Workflow Builder</h1>
      <p className="cs-help" style={{ marginTop: 6 }}>
        Visual block workflows: inputs, LLM transforms, branch filters and
        loops, wired on a canvas and executed on ephemeral compute. Each run
        is cancellable from Processes.
      </p>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }} role="tablist" aria-label="Workflow filter">
        {[['all', 'All'], ['examples', 'Examples'], ['mine', 'Mine'], ['solutions', 'Solutions']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            className={`mf-chip${view === id ? ' is-info' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setView(id)}
          >
            {label}
          </button>
        ))}
        {view === 'examples' && (
          <span className="cs-help" style={{ fontSize: 12, alignSelf: 'center' }}>
            Branch, loop, recurse, stream — open one to read how it works, Duplicate to make it yours.
          </span>
        )}
        {view === 'solutions' && (
          <span className="cs-help" style={{ fontSize: 12, alignSelf: 'center' }}>
            A business service end to end: workflows and apps handing work to each other, checked against one set of requirements.
          </span>
        )}
      </div>

      {view === 'solutions' && (
        <section style={{ marginTop: 16 }}>
          <SolutionBoard org={org} canEdit={['member', 'admin', 'owner'].includes(myRole || 'owner')} />
        </section>
      )}
      {view !== 'solutions' && workspaces && !workspaces.length && (
        <section style={{ marginTop: 16 }}>
          <p className="cs-help" style={{ marginBottom: 8 }}>
            No workspace yet — your first workflow creates a “{DEFAULT_WORKSPACE_NAME}” workspace to hold it.
          </p>
          <NewWorkflowForm orgId={org.id} workspaces={workspaces} placeholder="Name your first workflow…" />
        </section>
      )}
      {view !== 'solutions' && (workspaces || []).map((ws) => (
        <WorkspacePipelines key={ws.id} orgId={org.id} ws={ws} view={view} />
      ))}
    </div>
  );
}

function WorkspacePipelines({ orgId, ws, view }) {
  const scope = { org: orgId, workspace: ws.id };
  const { docs: pipelines, error } = useEntityList('pipeline', scope);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const shown = (pipelines || []).filter((pl) =>
    view === 'all' ? true : view === 'examples' ? !!pl.example : !pl.example);
  if (view === 'examples' && pipelines && !shown.length) return null;

  const duplicate = async (pl) => {
    setBusy(pl.id);
    setErr('');
    try {
      const id = await createEntity('pipeline', scope, {
        name: `${pl.name.replace(/^Example:\s*/i, '')} (copy)`,
        description: pl.description || '',
        graph: pl.graph || { blocks: [], connections: [] },
        capabilities: pl.capabilities || [],
        requirements: pl.requirements || [],
        mode: pl.mode || 'batch',
        execution: pl.execution || {},
        env: pl.env || 'sandbox',
        learn: pl.learn || '',
        example: false,
        status: 'draft',
        createdBy: user.uid,
      });
      navigate(`/orgs/${orgId}/ws/${ws.id}/pipelines/${id}`);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy('');
    }
  };

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{ws.name}</h2>
        <Link to={`/orgs/${orgId}/ws/${ws.id}`} className="cs-help" style={{ fontSize: 12 }}>
          open workspace →
        </Link>
      </div>

      {view !== 'examples' && (
        <NewWorkflowForm orgId={orgId} workspaceId={ws.id} small style={{ marginTop: 10, maxWidth: 440 }} />
      )}

      {error && <p className="cs-form-error" style={{ marginTop: 10 }}>Could not load workflows: {error}</p>}
      {err && <p className="cs-form-error" style={{ marginTop: 10 }}>{err}</p>}
      {pipelines && !shown.length && (
        <p className="cs-help" style={{ marginTop: 10, fontSize: 13 }}>No workflows here yet.</p>
      )}
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {shown.map((pl) => (
          <div
            key={pl.id}
            className="cs-card"
            style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <Link
              to={`/orgs/${orgId}/ws/${ws.id}/pipelines/${pl.id}`}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}
            >
              <strong style={{ color: 'var(--text)' }}>{pl.name}</strong>
              {pl.example && <span className="mf-chip is-info">example</span>}
              {pl.mode === 'streaming' && <span className="mf-chip">streaming</span>}
              <span className="cs-help" style={{ fontSize: 12 }}>
                {(pl.graph?.blocks || []).length} blocks · {pl.status}
              </span>
              <span style={{ flex: 1 }} />
              {pl.lastRun && (
                <span className="cs-help" style={{ fontSize: 12 }}>
                  last run {new Date(pl.lastRun.finishedAt).toLocaleString()}
                </span>
              )}
            </Link>
            <button
              type="button"
              className="cs-btn cs-btn-ghost"
              style={{ fontSize: 12 }}
              disabled={busy === pl.id}
              onClick={() => duplicate(pl)}
              title={pl.example ? 'Copy this example into an editable workflow' : 'Copy this workflow'}
            >
              {busy === pl.id ? 'Copying…' : 'Duplicate'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
